import type { AppKitNetwork } from "@reown/appkit/networks";
import { createAppKit } from "@reown/appkit/react";
import { SolanaAdapter } from "@reown/appkit-adapter-solana/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { injected } from "wagmi";
import {
  APP_METADATA,
  CHAIN_IMAGES,
  getAppKitConfig,
  getProjectId,
  NETWORK_CONFIG,
} from "./config";

// Module-level state for lazy initialization
let wagmiAdapter: WagmiAdapter | null = null;
let solanaAdapter: SolanaAdapter | null = null;
let isAppKitInitialized = false;

/**
 * Initializes the Wagmi adapter for AppKit integration
 * Uses lazy initialization to avoid execution during module import
 * Only includes EVM networks (filters out Solana networks)
 */
export function initializeWagmiAdapter(): WagmiAdapter {
  if (!wagmiAdapter) {
    // Filter to only EVM networks (eip155 namespace)
    const evmNetworks = NETWORK_CONFIG.supportedNetworks.filter(
      (network) => network.chainNamespace === "eip155",
    ) as unknown as [AppKitNetwork, ...AppKitNetwork[]];

    wagmiAdapter = new WagmiAdapter({
      networks: evmNetworks,
      projectId: getProjectId() || "",
      connectors: [injected()],
    });
  }
  return wagmiAdapter;
}

/**
 * Initializes the Solana adapter for AppKit integration
 * Uses lazy initialization to avoid execution during module import
 */
export function initializeSolanaAdapter(): SolanaAdapter {
  if (!solanaAdapter) {
    solanaAdapter = new SolanaAdapter();
  }
  return solanaAdapter;
}

/**
 * Initializes AppKit with the provided adapters (Wagmi and Solana)
 * Uses guard to ensure AppKit is only initialized once
 * Both adapters should be initialized in the component and passed here for consistency
 */
export function initializeAppKit(wagmiAdapter: WagmiAdapter, solanaAdapter: SolanaAdapter): void {
  if (!isAppKitInitialized) {
    createAppKit({
      adapters: [wagmiAdapter, solanaAdapter],
      networks: NETWORK_CONFIG.supportedNetworks as unknown as [AppKitNetwork, ...AppKitNetwork[]],
      projectId: getProjectId() || "",
      metadata: APP_METADATA,
      features: getAppKitConfig().features,
      themeVariables: getAppKitConfig().themeVariables,
      termsConditionsUrl: getAppKitConfig().termsConditionsUrl,
      privacyPolicyUrl: getAppKitConfig().privacyPolicyUrl,
      // Provide custom chain images for networks without built-in presets
      chainImages: CHAIN_IMAGES,
    });
    isAppKitInitialized = true;
  }
}
